import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AdminSignupPage from "@/pages/AdminSignupPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminLayout from "@/components/AdminLayout";
import ResidentLayout from "@/components/ResidentLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import TicketsPage from "@/pages/admin/Tickets";
import ResidentsPage from "@/pages/admin/Residents";
import UnitsPage from "@/pages/admin/Units";
import GenerateDuesPage from "@/pages/admin/GenerateDues";
import AnnouncementsPage from "@/pages/admin/Announcements";
import PaymentsPage from "@/pages/admin/Payments";
import ExpenditurePage from "@/pages/admin/Expenditure";
import TransactionsPage from "@/pages/admin/Transactions";
import SettingsPage from "@/pages/admin/Settings";
import SuperAdminDashboard from "@/pages/superadmin/Dashboard";
import ResidentDashboard from "@/pages/resident/Dashboard";
import ResidentAnnouncements from "@/pages/resident/Announcements";
import PayDues from "@/pages/resident/PayDues";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === 'super_admin') return <Navigate to="/superadmin" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/app" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Detect Google Auth callback - check BEFORE routes render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup/admin" element={<AdminSignupPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Super Admin */}
      <Route path="/superadmin" element={
        <ProtectedRoute roles={['super_admin']}>
          <AdminLayout isSuperAdmin>
            <SuperAdminDashboard />
          </AdminLayout>
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><AdminDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/tickets" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><TicketsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/units" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><UnitsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/generate-dues" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><GenerateDuesPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/residents" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><ResidentsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><AnnouncementsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><PaymentsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/expenditure" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><ExpenditurePage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/transactions" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <TransactionsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute roles={['admin', 'super_admin']}>
          <AdminLayout><SettingsPage /></AdminLayout>
        </ProtectedRoute>
      } />

      {/* Resident routes */}
      <Route path="/app" element={
        <ProtectedRoute roles={['resident']}>
          <ResidentLayout><ResidentDashboard /></ResidentLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/announcements" element={
        <ProtectedRoute roles={['resident']}>
          <ResidentLayout><ResidentAnnouncements /></ResidentLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/pay-dues" element={
        <ProtectedRoute roles={['resident']}>
          <ResidentLayout><PayDues /></ResidentLayout>
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
